import React from "react";
import { motion } from "framer-motion";
import { BrainCircuitIcon, ExternalLinkIcon, FilmIcon, HeartIcon, PlayCircleIcon, BoxIcon } from "lucide-react";
import { masterSnaks } from "../../data/masterSnaks";
import { InteriorSlide, SlideProps } from "./SlideShell";
const LIVE_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/i5nBsi4HkM73Cc7BWd7HeH/Screenshot_2026-08-27_at_12.44.51.png';
const LIVE_SNAK_URL = 'https://content.snakker.io/snak/bCiBl6w9Pzv4pcbqgLjzbfL4SabBEalB/index2.html';
const COLUMNS = [{
  label: 'Snak',
  icon: BoxIcon
}, {
  label: 'Cognitive anchor',
  icon: BrainCircuitIcon
}, {
  label: 'Emotional payoff',
  icon: HeartIcon
}, {
  label: 'Core deck visual',
  icon: FilmIcon
}] as const;
export function SlideMasterSnaks({}: SlideProps) {
  return <InteriorSlide surface data-id="element-337" data-mp="70nbgyeu7fh8">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-338" data-mp="70vmslvbacqb">
        <div data-id="element-339" data-mp="1o963r2icn3qr">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-340" data-mp="1t5wssfc4t9ob">
            Content roadmap
          </p>
          <h2 className="text-[clamp(2rem,3.3vw,3.2rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-341" data-mp="1fe03sk5rffw5">
            Suggested further{' '}
            <span className="text-[var(--brand)]" data-id="element-342" data-mp="15g2jg7r0u38t">Master Snaks</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-343" data-mp="1afydpf9x2gb6">
          Five high-value stories anchored in existing scientific source visuals.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-344" data-mp="10iajcqg83atj">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="min-h-0 overflow-hidden rounded-3xl border border-[var(--brand)]/10 bg-white shadow-sm lg:col-span-9" aria-label="Suggested Master Snaks roadmap" data-id="element-345" data-mp="2qt57nrhmnkhc">
          <div className="grid grid-cols-[0.8fr_1fr_1fr_2.6fr] bg-[var(--brand)] px-5 py-3.5 text-white" data-id="element-346" data-mp="2pueb1l2ybd9">
            {COLUMNS.map(column => {
            const Icon = column.icon;
            return <div key={column.label} className="flex items-center gap-2 pr-3" data-id="element-347" data-mp="3pua533q6loeo">
                  <Icon className="h-3.5 w-3.5 text-white/70" aria-hidden="true" data-id="element-348" data-mp="qc0wggo7x7h7" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-349" data-mp="mcrzl2xvsthf">
                    {column.label}
                  </p>
                </div>;
          })}
          </div>

          <div className="flex h-[calc(100%-2.75rem)] flex-col divide-y divide-[var(--brand)]/10" data-id="element-350" data-mp="cmpahdtvrzod">
            {masterSnaks.map((snak, index) => <motion.article key={snak.name} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.28 + index * 0.08,
            duration: 0.42
          }} className="grid min-h-0 flex-1 grid-cols-[0.8fr_1fr_1fr_2.6fr] items-center px-5 py-2.5" data-id="element-351" data-mp="1a2uo592l7hba" data-mpi={index}>
                <div className="pr-3" data-id="element-352" data-mp="1mjno91oz9l96">
                  <span className="inline-flex rounded-lg bg-[var(--brand)]/10 px-2.5 py-1.5 text-xs font-extrabold text-[var(--brand)]" data-id="element-353" data-mp="1milzjuf1h08z">
                    {snak.name}
                  </span>
                </div>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-354" data-mp="1vihe897szddr">
                  {snak.cognitiveAnchor}
                </p>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-355" data-mp="25bg5jyuopi12">
                  {snak.emotionalPayoff}
                </p>
                <div className="min-w-0" data-id="element-356" data-mp="2abawu3idg9mn">
                  <p className="text-xs font-bold leading-snug text-[var(--fg)]" data-id="element-357" data-mp="2ebutz72y3d8s">
                    {snak.visualLead}
                    <span className="font-medium text-[var(--muted)]" data-id="element-358" data-mp="2jay0kvykpriv">
                      {' '}— {snak.visualDetail}
                    </span>
                    <span className="ml-1.5 whitespace-nowrap text-[10px] font-bold text-[var(--brand)]" data-id="element-359" data-mp="21lu5ciojm7xv">
                      ({snak.deckPage})
                    </span>
                  </p>
                  <span className="mt-1.5 inline-flex max-w-full truncate rounded-full bg-[var(--surface)] px-2.5 py-1 text-[9px] font-semibold text-[var(--muted)]" data-id="element-360" data-mp="2ae5vs4jnpvdu">
                    {snak.source}
                  </span>
                </div>
              </motion.article>)}
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-3" data-id="element-361" data-mp="1wmhnkccj3r25">
          <div className="mb-3 flex items-center justify-between" data-id="element-362" data-mp="380cyta0bbfo3">
            <div data-id="element-363" data-mp="333mkar45os4s">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-364" data-mp="3321pd0dxj76e">
                Live example
              </p>
              <h3 className="mt-1 text-lg font-extrabold text-[var(--fg)]" data-id="element-365" data-mp="oxetrnlxvgek">Interactive Snak</h3>
            </div>
            <ExternalLinkIcon className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-366" data-mp="funh5khbuelc" />
          </div>

          <a href={LIVE_SNAK_URL} target="_blank" rel="noopener noreferrer" aria-label="Open the live interactive Snak in a new tab" className="group relative min-h-0 flex-1 overflow-hidden rounded-[1.75rem] bg-[#006b3c] shadow-[0_18px_45px_rgba(23,32,51,0.18)] outline-none ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(23,32,51,0.24)] focus-visible:ring-4 focus-visible:ring-[var(--brand)]/35" data-id="element-367" data-mp="ksyrrxfg4t4m">
            <img src={LIVE_SNAK_IMAGE} alt="Interactive Snak asking which outcome indicates a deep and clinically meaningful response" className="h-full w-full object-contain" data-id="element-368" data-mp="13r1lgpixve8r" />
            <span className="absolute inset-x-4 bottom-4 flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-extrabold text-[var(--brand)] shadow-lg transition group-hover:bg-[var(--brand)] group-hover:text-white" data-id="element-369" data-mp="1w8vqueh00tfk">
              <PlayCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-370" data-mp="213gc94hqvv2b" />
              Open live Snak
            </span>
          </a>
        </motion.aside>
      </div>
    </InteriorSlide>;
}